import { useCallback } from 'react';
import { useAsync } from './useAsync';
import type { AsyncState } from './useAsync';
import { getTrip, listBalances, listMembers } from '../data/trips';
import { listExpenses, listSettlements } from '../data/expenses';
import { loadRates } from '../data/rates';
import type { KnownRate } from '../data/rates';
import type { BalanceRow, ExpenseRow, SettlementRow, TripMemberRow, TripRow } from '../data/types';

export interface TripData {
  trip: TripRow;
  members: TripMemberRow[];
  expenses: ExpenseRow[];
  balances: BalanceRow[];
  settlements: SettlementRow[];
  rates: Map<string, KnownRate>;
}

/**
 * Everything one trip screen needs, in one place.
 *
 * The trip is fetched first because the rates query needs its base currency;
 * the other four are independent and go out together. Five round trips on
 * open sounds like a lot until you notice there is no server of ours in the
 * path — these are five parallel HTTP requests straight to Postgres, and the
 * alternative is a view or an RPC that would have to be kept in step with
 * every screen that reads it.
 */
export function useTripData(tripId: string): AsyncState<TripData> {
  const loader = useCallback(async (): Promise<TripData> => {
    const trip = await getTrip(tripId);

    const [members, expenses, balances, settlements, rates] = await Promise.all([
      listMembers(tripId),
      listExpenses(tripId),
      listBalances(tripId),
      listSettlements(tripId),
      loadRates(trip.base_currency),
    ]);

    return { trip, members, expenses, balances, settlements, rates };
  }, [tripId]);

  return useAsync(loader, tripId);
}
